import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import { FileText, ThumbsUp, X } from "lucide-react";
import { addMyDiaryApi } from "@services/MarketWorking/MyTeam/addMyDiaryApi";
import ModalPopup from "@components/Common/ModalPopup";
import { connect } from "react-redux";
import Loader from "@features/shared/components/Loader";
import { compressImage } from "@components/Common/Function";

const MyDiary = ({
  addMyDiaryApi,
  selectedDealerCode,
  selectedWssCode,
  selectedDealerWSS,
  isOpen,
  setIsOpen
}) => {
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [diaryData, setDiaryData] = useState({
    Title: "",
    Observation: "",
  });
  const [file, setFile] = useState();
  const [loading, setLoading] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [formValidation, setFormValidation] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  const FileHandler = useCallback(async (e) => {
    setIsUploading(true);
    setFormValidation({});
    if (e.target.files[0]?.size < 5242880) {
      let image = "";
      if (e.target.files[0]?.name?.split(".")?.slice(-1) !== "pdf") {
        image = await compressImage(e.target.files[0]);
      }
      const reader = new FileReader();
      reader.onloadend = async () => {
        setFile({
          base64: reader.result.replace("data:", "").replace(/^.+,/, ""),
          fileExtension: e.target.files[0]?.name
            ?.split(".")
            ?.slice(-1)
            ?.toString(),
          fileName: e.target.files[0]?.name,
        });
      };
      if (!_.isEmpty(image)) {
        reader.readAsDataURL(image);
      } else {
        reader.readAsDataURL(e.target.files[0]);
      }
    } else {
      setFile({
        base64: "",
        fileExtension: "",
        fileName: "",
      });
      setFormValidation({
        file: "Please upload the file whose size is less than 5MB",
      });
    }
    setIsUploading(false);
  }, []);

  const onChange = useCallback((e) => {
    setDiaryData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const onSubmit = useCallback((e) => {
    e.preventDefault();
    setIsSubmit(true);
    const errors = {};
    if (!diaryData.Observation) {
      errors.Observation = "Observation is required";
    }
    if (!(selectedDealerCode || selectedWssCode)) {
      errors.file = "Please select dealer or WSS code";
    }
    if (formValidation.file) {
      errors.file = "Please upload the file whose size is less than 5MB";
    }
    setFormValidation(errors);
  }, [diaryData, selectedDealerCode, selectedWssCode, formValidation.file]);

  //post api call of my Diary
  useEffect(() => {
    if (Object?.keys(formValidation)?.length === 0 && isSubmit) {
      (async () => {
        const data = {
          Observation: diaryData.Observation,
          fileExtension: file?.fileExtension,
          fileName: file?.fileName,
          base64: file?.base64,
          dealerCode: selectedDealerCode,
          wssCode: selectedWssCode,
        };

        const {
          Observation,
          fileExtension,
          fileName,
          base64,
          dealerCode,
          wssCode,
        } = data;
        setLoading(true);
        const response = await addMyDiaryApi({
          RecordtypeName: selectedDealerCode ? "Visit Plan" : "Visit Plan WSS",
          Observation,
          fileExtension,
          fileName,
          base64,
          wssCode,
          dealerCode,
        });
        setLoading(false);
        if (response?.success) {
          setIsSubmitModalOpen(true);
          setIsSubmit(false);
          setTimeout(() => setIsOpen(false), 1500);
        }
        setDiaryData({
          Observation: "",
        });
        setFile({
          base64: "",
          fileExtension: "",
          fileName: "",
        });
      })();
    }
    if (Object?.keys(formValidation)?.length !== 0) {
      setIsSubmit(false);
    }
  }, [isSubmit, formValidation, diaryData.Observation, file, selectedDealerCode, selectedWssCode, addMyDiaryApi, setIsOpen]);

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="relative w-full max-w-6xl mx-4 bg-white rounded-lg shadow-xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-base font-semibold text-[#3901FF]">My Diary</h2>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 max-h-[40vh] overflow-auto">
              <div className="w-auto">
                <Loader loading={isUploading} />
                {isSubmit && Object?.keys(formValidation)?.length === 0 && (
                  <Loader loading={loading} />
                )}

                <div className="w-full">
                  <input
                    type="text"
                    disabled
                    value={
                      selectedDealerWSS
                        ? ` ${selectedDealerWSS} : ${
                            selectedDealerCode || selectedWssCode
                          } `
                        : ""
                    }
                    className={`border ${
                      !diaryData?.Title && formValidation?.Title
                        ? "border-[#EF5464]"
                        : "border-[#808080]"
                    } rounded-[0.3rem] w-full pl-3 font-roboto text-[0.731rem] leading-[0.688rem] text-[#2B2B2B] h-[1.7rem] mb-2.5`}
                  />

                  {!diaryData?.Title && formValidation?.Title && (
                    <p className="text-red-500 text-[0.731rem] py-1.5 whitespace-nowrap">
                      {formValidation?.Title}
                    </p>
                  )}

                  <textarea
                    name="Observation"
                    rows="8"
                    value={diaryData?.Observation}
                    placeholder="Enter your observation here"
                    className={`resize-none border ${
                      !diaryData?.Observation && formValidation?.Observation
                        ? "border-[#EF5464]"
                        : "border-[#808080]"
                    } rounded-[0.3rem] w-full font-roboto text-[0.731rem] leading-[0.688rem] text-[#808080] p-3 mb-1.5 focus:outline-none`}
                    onChange={onChange}
                  />

                  {!diaryData?.Observation && formValidation?.Observation && (
                    <p className="text-red-500 text-[0.731rem] py-1.5">{formValidation?.Observation}</p>
                  )}
                </div>

                {!_.isEmpty(file?.fileName) && (
                  <div className="flex items-center pl-1 mb-3">
                    <FileText size={12} className="font-semibold" />
                    <span className="text-xs font-semibold ml-2">{file?.fileName}</span>
                  </div>
                )}

                {formValidation?.file && (
                  <p className="text-red-500 text-[0.731rem] py-1.5">{formValidation?.file}</p>
                )}

                <div className="grid grid-cols-12 gap-4 mt-2">
                  <div className="col-span-6"></div>
                  <div className="col-span-3">
                    <div className="relative overflow-hidden cursor-pointer font-roboto text-[0.668rem] leading-[0.688rem] text-[#2CC26A] mt-2 text-right">
                      Add file
                      <input
                        className="cursor-pointer h-full absolute top-0 right-0 opacity-0"
                        type="file"
                        accept=".jpg,.png,.pdf, .jpeg"
                        onChange={FileHandler}
                      />
                    </div>
                  </div>
                  <div className="col-span-3">
                    <button
                      className="bg-[#2CC26A] rounded-[0.3rem] border-[#2CC26A] text-[#F9F9F9] font-roboto text-[0.606rem] leading-[0.688rem] whitespace-nowrap w-full py-2 hover:bg-[#28b05f] transition-colors focus:outline-none"
                      onClick={onSubmit}
                    >
                      Submit
                    </button>
                  </div>
                </div>

                {isSubmitModalOpen && (
                  <ModalPopup
                    className="ModalPopup_Submitted"
                    isModalOpen={isSubmitModalOpen}
                    setIsModalOpen={setIsSubmitModalOpen}
                    title="Submitted Successfully"
                    data="My Diary has been submitted successfully."
                    backdrop={true}
                    fade={false}
                    icon={
                      <div className="submittedIcons draftIcons">
                        <ThumbsUp className="submittedSmall" />
                        <ThumbsUp className="submittedLarge" />
                        <ThumbsUp className="submittedSmall" />
                      </div>
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default connect(null, {
  addMyDiaryApi,
})(MyDiary);
